import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { ContentfulSmLesson } from 'src/graphql/queries/GetSmLesson';
import {
  ContentfulLearningPathWithNavigation,
  ContentfullCourse,
} from 'src/graphql/queries/GetLearningPathWithNavigation';
import { getSlug } from 'src/utils/slug';

type CourseLesson =
  | NonNullable<ContentfulSmCourse['lessonsCollection']['items'][0]>
  | NonNullable<ContentfullCourse['lessonsCollection']['items'][0]>;

export interface NextLessonCandidate {
  lesson: CourseLesson;
  courseSlug: string;
}

type CourseWithLessons = ContentfulSmCourse | ContentfullCourse | undefined;

const mapCourseLessons = (course?: CourseWithLessons): NextLessonCandidate[] => {
  if (!course?.lessonsCollection?.items?.length) {
    return [];
  }

  const courseSlug = getSlug(course);

  return course.lessonsCollection.items
    .filter((lesson): lesson is CourseLesson => Boolean(lesson))
    .map((lesson) => ({
      lesson,
      courseSlug,
    }));
};

export const getOrderedLessons = (
  learningPath?: ContentfulLearningPathWithNavigation,
  course?: ContentfulSmCourse,
): NextLessonCandidate[] => {
  const pathCourses =
    learningPath?.coursesCollection?.items?.filter(
      (pathCourse): pathCourse is ContentfullCourse => Boolean(pathCourse),
    ) || [];

  if (pathCourses.length > 0) {
    return pathCourses.flatMap((pathCourse) => mapCourseLessons(pathCourse));
  }

  return mapCourseLessons(course);
};

export const getNextLesson = (
  course?: ContentfulSmCourse,
  lesson?: ContentfulSmLesson,
  learningPath?: ContentfulLearningPathWithNavigation,
): NextLessonCandidate | null => {
  const orderedLessons = getOrderedLessons(learningPath, course);

  if (!lesson || orderedLessons.length === 0) {
    return null;
  }

  const currentLessonIndex = orderedLessons.findIndex((item) => item.lesson.sys.id === lesson.sys.id);

  if (currentLessonIndex === -1) {
    return orderedLessons[0] || null;
  }

  const nextLesson = orderedLessons[currentLessonIndex + 1];

  if (nextLesson) {
    return nextLesson;
  }

  const hasLearningPathContext = Boolean(learningPath?.coursesCollection?.items?.length);

  return hasLearningPathContext ? null : orderedLessons[0] || null;
};
